import React from 'react';
import { AlertTriangle, Heart, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { client, urlFor, getImageAlt } from '../../lib/sanity';
import { getPetUrlPath } from '../../lib/petUtils';
import type { Pet } from '../../types/sanity';

export function UrgentNeedsBanner() {
  const [urgentPets, setUrgentPets] = React.useState<Pet[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [currentPet, setCurrentPet] = React.useState(0);

  React.useEffect(() => {
    const fetchUrgentPets = async () => {
      try {
        const result = await client.fetch<Pet[]>(`
          *[_type == "pet" && isUrgentFundraising == true && !(_id in path("drafts.**"))] | order(_createdAt desc) {
            _id,
            name,
            slug,
            image,
            fundraisingGoal,
            fundraisingCurrent,
            fundraisingDescription,
            mainCategory
          }
        `);
        setUrgentPets(result);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching urgent pets:', err);
        setLoading(false);
      }
    };

    fetchUrgentPets();
  }, []);

  React.useEffect(() => {
    if (urgentPets.length > 1) {
      const interval = setInterval(() => {
        setCurrentPet((prev) => (prev + 1) % urgentPets.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [urgentPets.length]);

  if (loading || urgentPets.length === 0) {
    return null;
  }

  const pet = urgentPets[currentPet];
  const percentage = pet.fundraisingGoal ? Math.round((pet.fundraisingCurrent || 0) / pet.fundraisingGoal * 100) : 0;

  return (
    <section className="py-8 bg-gradient-to-r from-accessible-red-600 to-accessible-red-700 high-contrast:bg-black relative overflow-hidden">
      <div className="absolute inset-0 bg-black/10 high-contrast:bg-transparent"></div>
      
      <div className="relative max-w-7xl mx-auto px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-6 text-white dark:text-white high-contrast:text-white">
            <div className="relative">
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white high-contrast:border-white shadow-lg">
                <img
                  src={urlFor(pet.image).width(128).height(128).url()}
                  alt={getImageAlt(pet.image, pet)}
                  className="w-full h-full object-cover high-contrast:filter high-contrast:grayscale high-contrast:contrast-150"
                />
              </div>
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white dark:bg-white high-contrast:bg-white text-accessible-red-600 dark:text-accessible-red-600 high-contrast:text-black px-4 py-1 rounded-full text-sm font-bold shadow-md">
                {pet.name}
              </div>
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-300 dark:text-yellow-300 high-contrast:text-white animate-pulse drop-shadow-md" />
                  <h2 className="heading-3-white mb-0 text-shadow-sm ml-2">
                    PILNA ZBIÓRKA
                  </h2>
                </div>
              </div>
              
              <p className="body-large-white mb-0 text-shadow-sm">
                <strong className="text-yellow-200 dark:text-yellow-200 high-contrast:text-white">{pet.fundraisingDescription}</strong>
              </p>
              
              <div className="flex items-center gap-2 mt-2">
                <div className="text-white dark:text-white high-contrast:text-white font-semibold">
                  {(pet.fundraisingCurrent || 0).toLocaleString('pl-PL')} zł / {(pet.fundraisingGoal || 0).toLocaleString('pl-PL')} zł
                </div>
                <div className="text-yellow-200 dark:text-yellow-200 high-contrast:text-white font-bold">
                  ({percentage}%)
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/wplata-jednorazowa"
              className="bg-yellow-400 dark:bg-yellow-400 high-contrast:bg-white text-accessible-gray-900 dark:text-accessible-gray-900 high-contrast:text-black px-4 py-2 rounded-lg hover:bg-yellow-300 dark:hover:bg-yellow-300 high-contrast:hover:bg-accessible-gray-200 transition-all duration-300 transform hover:scale-105 font-bold shadow-lg flex items-center gap-2"
            >
              <Heart className="w-5 h-5" />
              <span>POMÓŻ {pet.name.toUpperCase()}</span>
            </Link>
            <Link
              to={getPetUrlPath(pet)}
              className="bg-white/20 dark:bg-white/20 high-contrast:bg-white text-white dark:text-white high-contrast:text-black px-3 py-2 rounded-lg hover:bg-white/30 dark:hover:bg-white/30 high-contrast:hover:bg-accessible-gray-200 transition-colors backdrop-blur-sm border border-white/30 dark:border-white/30 high-contrast:border-black flex items-center gap-2"
            >
              <span>Zobacz profil</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-4 bg-white/20 dark:bg-white/20 high-contrast:bg-white rounded-full h-2 overflow-hidden">
          <div 
            className="h-full bg-yellow-400 dark:bg-yellow-400 high-contrast:bg-black rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
        
        {urgentPets.length > 1 && (
          <div className="flex justify-center gap-2 mt-3">
            {urgentPets.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPet(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentPet ? 'bg-yellow-400 dark:bg-yellow-400 high-contrast:bg-white scale-125' : 'bg-white/40 dark:bg-white/40 high-contrast:bg-white/60'
                }`}
                aria-label={`Przejdź do zbiórki ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}